// Build structured reconcile reports from match results.
// Reports are stored as JSON in invoices.reconcile_report.

import { sumAmounts, moneyEq, absRound, MONEY_TOLERANCE } from '../../calc.ts';
import type { InvoiceLink } from '@shared/types/invoice.ts';
import type { MatchResult, NearMissCandidate, UnmatchedReason } from './matching/types.ts';

// Re-export matching types used by consumers of the report shape
export type { NearMissCandidate, UnmatchedReason } from './matching/types.ts';

// ── Report types (self-contained) ───────────────────────────────────────

export type ReportResult = 'approved' | 'partial' | 'unmatched';

export type ViolationType =
  | 'unmatched_line_item'
  | 'p1_field_mismatch'
  | 'invoice_total_mismatch';

export interface ReportViolation {
  type: ViolationType;
  ordinal?: number;
  details: Record<string, unknown>;
}

export interface ReportLineItem {
  ordinal: number;
  match: 'matched' | 'unmatched';
  priority: 'P1' | 'P2' | null;
  matched_candidates: {
    type?: string;
    entity_id?: number;
    side?: string;
    reference_number?: string;
    net_amount?: number;
    date?: string;
    weight?: number;
  }[];
  field_comparison?: {
    field: string;
    expected: string | number;
    actual: string | number;
  }[];
  /**
   * Machine-readable failure code — present only for unmatched items.
   */
  reason?: UnmatchedReason;
  /**
   * Up to 3 candidates considered but not matched — present only for unmatched items
   * that had at least one candidate considered.
   */
  near_misses?: NearMissCandidate[];
  /**
   * What the operator should do next to resolve this unmatched line item.
   *
   * - `link_manually`            — no automated path found; use manual link UI.
   * - `request_correction`       — P1 verify failed; the invoice amounts look wrong.
   * - `complete_transaction_data`— OUR transaction data is incomplete (e.g. an unweighed
   *                                per-ton candidate); fix the transaction, don't blame
   *                                the vendor.
   */
  suggested_action?: 'link_manually' | 'request_correction' | 'complete_transaction_data';
}

export interface TotalCheck {
  status: 'ok' | 'mismatch';
  expected: { net: number; vat: number; gross: number };
  actual: { net: number; vat: number; gross: number };
}

export interface ReconcileReportOutput {
  timestamp: string;
  result: ReportResult;
  line_items: ReportLineItem[];
  total_check: TotalCheck | null;
  violations: ReportViolation[];
  /** True when ≥1 line matched by reference but the invoice amounts disagree with the
   *  linked transaction(s) — drives review_status='request_correction' + the UI status. */
  requires_correction: boolean;
  /** Human-readable note for reports the matcher wrote for a no-op (e.g. an invoice whose
   *  line items all zeroed to nothing to reconcile). Present so the invoice ALWAYS carries a
   *  non-null reconcile_report and never re-enters the approved-unswept pool (finding 3). */
  note?: string;
  /** True when the RPC skipped ≥1 link insert for this invoice because a concurrent write
   *  had already claimed the side (freshness guard). Surfaced so a dropped link is never
   *  silent (finding 4). */
  guard_skipped?: number;
}

/** No-op reconcile report — for an invoice the sweep processed but had nothing to match
 *  (e.g. every line item zeroed out by corrections). Writing THIS (never null) keeps the
 *  invoice out of the approved-unswept pool (`reconcile_report IS NULL`) so it isn't
 *  re-swept every run (finding 3). */
export function buildNoopReconcileReport(note: string): ReconcileReportOutput {
  return {
    timestamp: new Date().toISOString(),
    result: 'approved',
    line_items: [],
    total_check: null,
    violations: [],
    requires_correction: false,
    note,
  };
}

// ── Input ───────────────────────────────────────────────────────────────

export interface ReportInput {
  matchResult: MatchResult;
  invoiceTotals?: { net: number; vat: number; gross: number };
  candidates?: InvoiceLink[];
}

// ── Suggested-action mapping ──────────────────────────────────────────────

/** Map an unmatched reason to the operator's next step. Reasons that mean OUR data is
 *  incomplete (unweighed / unpriced transaction) point at fixing the transaction, not at
 *  a manual link or a vendor correction. Verify failures on a P1 ref point at a vendor
 *  correction; everything else is a manual link. */
function suggestedActionFor(reason: UnmatchedReason | undefined): ReportLineItem['suggested_action'] {
  switch (reason) {
    case 'p1_verify_failed':
      return 'request_correction';
    case 'candidate_unweighed':
    case 'ref_found_tx_unpriced':
      return 'complete_transaction_data';
    // p2_ambiguous: a verified subset existed but a disjoint one also matched — the
    // operator picks the right combination manually (same action as p2_no_subset).
    case 'p2_ambiguous':
    case 'p2_no_subset':
      return 'link_manually';
    default:
      return 'link_manually';
  }
}

// ── Builder ─────────────────────────────────────────────────────────────

export function buildReconcileReport(input: ReportInput): ReconcileReportOutput {
  const { matchResult, invoiceTotals, candidates } = input;
  const violations: ReportViolation[] = [];

  // Candidate lookup for enriching report
  const candidateLookup = new Map<string, InvoiceLink>();
  for (const c of candidates ?? []) {
    candidateLookup.set(`${c.entity_type}:${c.entity_id}:${c.side ?? ''}`, c);
  }

  // Group links by line item ID
  const linksByLi = new Map<number, InvoiceLink[]>();
  for (const link of matchResult.links) {
    const lid = link.invoice_line_item_id ?? 0;
    const bucket = linksByLi.get(lid);
    if (bucket) bucket.push(link);
    else linksByLi.set(lid, [link]);
  }

  // Build per-line-item reports
  const lineItemReports: ReportLineItem[] = matchResult.line_item_reasons.map((r, idx) => {
    const ordinal = idx + 1;
    const itemLinks = linksByLi.get(r.line_item_id) ?? [];

    const matchedCandidates = itemLinks.map(link => {
      const entry: ReportLineItem['matched_candidates'][number] = {};
      entry.type = link.entity_type;
      entry.entity_id = link.entity_id;
      if (link.side) entry.side = link.side;

      const matched = candidateLookup.get(`${link.entity_type}:${link.entity_id}:${link.side ?? ''}`);
      if (matched?.reference_number) entry.reference_number = matched.reference_number;
      if (matched?.date) entry.date = matched.date;
      if (matched?.weight != null) entry.weight = matched.weight;
      if (link.pricing.net_amount != null) entry.net_amount = absRound(link.pricing.net_amount);

      return entry;
    });

    const report: ReportLineItem = {
      ordinal,
      // correction_required lines carry firm links → reported as 'matched'; the
      // request_correction signal rides on suggested_action + field_comparison below.
      match: (r.match === 'approved' || r.match === 'correction_required') ? 'matched' : r.match,
      priority: r.priority ?? null,
      matched_candidates: matchedCandidates,
    };


    // Add field comparisons when P1 verify produced mismatches (unmatched with verifyResult)
    if (r.verify_result && r.verify_result.mismatches.length > 0) {
      report.field_comparison = r.verify_result.mismatches.map(m => ({
        field: m.field, expected: m.expected, actual: m.actual,
      }));
      violations.push({ type: 'p1_field_mismatch', ordinal, details: { comparisons: report.field_comparison } });
    }

    // Matched-but-wrong-amounts: firm link emitted, invoice needs a vendor correction.
    if (r.match === 'correction_required') {
      report.suggested_action = 'request_correction';
    }

    if (r.match === 'unmatched') {
      // Populate unmatched reason + near-misses
      if (r.unmatched_reason) report.reason = r.unmatched_reason;
      if (r.near_miss_candidates && r.near_miss_candidates.length > 0) {
        report.near_misses = r.near_miss_candidates;
      }

      // Determine suggested_action (suggestions are request-time only now — never persisted).
      report.suggested_action = suggestedActionFor(r.unmatched_reason);

      // unmatched_line_item only when there's no P1 verifyResult (pure miss, not a verify failure)
      if (!r.verify_result) {
        violations.push({ type: 'unmatched_line_item', ordinal, details: { ordinal, reason: r.unmatched_reason ?? null } });
      }
    }

    return report;
  });

  // Total check: compare invoice totals vs sum of matched link amounts
  let totalCheck: TotalCheck | null = null;
  if (invoiceTotals && matchResult.links.length > 0) {
    const actual = sumAmounts(matchResult.links.map(l => l.pricing));
    const isMismatch =
      !moneyEq(invoiceTotals.net, actual.net, MONEY_TOLERANCE) ||
      !moneyEq(invoiceTotals.vat, actual.vat, MONEY_TOLERANCE) ||
      !moneyEq(invoiceTotals.gross, actual.gross, MONEY_TOLERANCE);
    totalCheck = { status: isMismatch ? 'mismatch' : 'ok', expected: invoiceTotals, actual: { net: actual.net, vat: actual.vat, gross: actual.gross } };
    if (isMismatch) {
      violations.push({ type: 'invoice_total_mismatch', details: { expected: invoiceTotals, actual } });
    }
  }

  const result: ReportResult = matchResult.status === 'approved' ? 'approved'
    : matchResult.links.length > 0 ? 'partial' : 'unmatched';

  return {
    timestamp: new Date().toISOString(),
    result,
    line_items: lineItemReports,
    total_check: totalCheck,
    violations,
    requires_correction: matchResult.requiresCorrection ?? false,
  };
}
